import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type BadgeTone = 'neutral' | 'brand' | 'brandSolid' | 'green' | 'amber' | 'red' | 'blue';

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-chip text-ink-strong',
  brand: 'bg-tint text-tint-fg',
  // Solid, high-emphasis — for the top-tier badge (e.g. install-wide Tenant Admin), so it reads
  // as clearly stronger than the light-tinted `brand` (Brand Workspace Admin) and `neutral`.
  brandSolid: 'bg-action text-action-fg',
  green: 'bg-success-tint dark:bg-success-dark-tint text-success dark:text-success-dark',
  amber: 'bg-amber-50 dark:bg-warning-dark-tint text-amber-700 dark:text-warning-dark',
  red: 'bg-red-50 dark:bg-error-dark-tint text-red-700 dark:text-error-dark',
  // The UI has no blue any more: the old blue tone is the neutral tint, like `brand`.
  blue: 'bg-tint text-tint-fg',
};

type BadgeSize = 'sm' | 'lg' | 'xl';

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs font-medium',
  lg: 'px-3.5 py-1.5 text-sm font-semibold',
  // Headline status pill — ~2.5× the default, for a state that must dominate (e.g. a campaign's
  // review status in the page header).
  xl: 'px-5 py-2 text-3xl font-bold',
};

export function Badge({
  tone = 'neutral',
  size = 'sm',
  children,
}: {
  tone?: BadgeTone;
  /** 'lg' = a prominent status pill; 'xl' = a headline status pill (~2.5× default). */
  size?: BadgeSize;
  children: ReactNode;
}) {
  return (
    <span
      className={cn('inline-flex items-center rounded-full', sizeClasses[size], toneClasses[tone])}
    >
      {children}
    </span>
  );
}
